import { decodeJWT } from "../../../lib/iprs-react-library/src";

const REFERRER_ROLE = "Referrer";

/**
 * Returns the roles in a token as an array of strings, for the portal home
 * page buttons.
 *
 * The roles claim comes in different shapes depending on how the token was
 * made:
 * - Login tokens (IPRSTokenCreator) write "roles" twice, so it decodes as an
 *   array of comma-joined strings, e.g. ["Referrer,MIReports", "Referrer,MIReports"].
 * - Open access links made with the wiki dotnet-script, or by hand in the
 *   past, write it once, so it decodes as a plain comma-joined string, e.g.
 *   "User,Referrer" or "Report Receiver (attachment),guest".
 * - Open access links made by the CreateAccessToken API (GetRefferalToken)
 *   have no roles at all.
 *
 * The old layout sent every user straight to the referral form without
 * checking roles, and open access links rely on that: some carry no roles
 * and some carry roles without Referrer (the AHG link, for example). So a
 * link token always gets Referrer added. Login tokens keep their real roles.
 */
export const getPortalRoles = (token) => {
    const payload = decodeJWT(token);
    const roles = payload?.roles;

    let roleList = [];
    if (Array.isArray(roles)) {
        roleList = roles.filter((role) => typeof role === "string");
    } else if (typeof roles === "string") {
        roleList = [roles];
    }
    roleList = roleList.filter((role) => role.trim() !== "");

    const hasReferrer = roleList.some((role) =>
        role.split(",").some((name) => name.trim() === REFERRER_ROLE));

    return isOpenAccessLink(payload) && !hasReferrer
        ? [...roleList, REFERRER_ROLE]
        : roleList;
};

/**
 * A token from a link, rather than a login, for a known user. Only the login
 * path (GetUserToken) sets "amr" (pwd, plus mfa), so a token without it came
 * from a link. Requiring a UserID leaves out the ClientList-only tokens used
 * by /AddReferrer, which name no referrer.
 */
const isOpenAccessLink = (payload) =>
    !!payload && payload.amr === undefined && !!payload.UserID;
